import { Lens } from './../lens';
import { structEq } from './../equals';
import { Subscription, BehaviorSubject, combineLatest } from 'rxjs';
export class AbstractReadOnlyAtom extends BehaviorSubject {
    view(...args) {
        /* eslint-disable @typescript-eslint/no-use-before-define */
        return args[0] !== undefined
            // view(getter) case
            ? typeof args[0] === 'function'
                ? new AtomViewImpl(this, args[0])
                // view('key') case
                : typeof args[0] === 'string'
                    ? new AtomViewImpl(this, Lens.compose(...args.map(Lens.key())).get)
                    // view(lens) and view(prism) cases
                    // @NOTE single case handles both lens and prism arg
                    : new AtomViewImpl(this, x => args[0].get(x))
            // view() case
            : this;
        /* eslint-enable @typescript-eslint/no-use-before-define */
    }
}
export class AbstractAtom extends AbstractReadOnlyAtom {
    set(x) {
        this.modify(() => x);
    }
    lens(arg1, ...args) {
        /* eslint-disable @typescript-eslint/no-use-before-define */
        // lens(prop expr) case
        return typeof arg1 === 'function'
            ? new LensedAtom(this, Lens.prop(arg1), structEq)
            // lens('key') case
            : typeof arg1 === 'string'
                ? new LensedAtom(this, Lens.compose(Lens.key(arg1), ...args.map(k => Lens.key(k))), structEq)
                // lens(lens) case
                : new LensedAtom(this, arg1, structEq);
        /* eslint-enable @typescript-eslint/no-use-before-define */
    }
}
export class JsonAtom extends AbstractAtom {
    constructor(initialValue) {
        super(initialValue);
    }
    get() {
        return this.getValue();
    }
    modify(updateFn) {
        const prevValue = this.getValue();
        const next = updateFn(prevValue);
        if (!structEq(prevValue, next))
            this.next(next);
    }
    set(x) {
        const prevValue = this.getValue();
        if (!structEq(prevValue, x))
            this.next(x);
    }
}
class LensedAtom extends AbstractAtom {
    _source;
    _lens;
    _eq;
    constructor(_source, _lens, _eq = structEq) {
        // @NOTE this is a major hack to optimize for not calling
        // _lens.get the extra time here. This makes the underlying
        // BehaviorSubject to have an `undefined` for it's current value.
        //
        // But it works because before somebody subscribes to this
        // atom, it will subscribe to the _source (which we expect to be a
        // descendant of BehaviorSubject as well), which will emit a
        // value right away, triggering our _onSourceValue.
        super(undefined);
        this._source = _source;
        this._lens = _lens;
        this._eq = _eq;
    }
    get() {
        // Optimization: in case we're already subscribed to the
        // source atom, the BehaviorSubject.getValue will return
        // an up-to-date computed lens value.
        //
        // This way we don't need to recalculate the lens value
        // every time.
        return this._subscription
            ? this.getValue()
            : this._lens.get(this._source.get());
    }
    modify(updateFn) {
        this._source.modify(x => this._lens.modify(updateFn, x));
    }
    set(newValue) {
        this._source.modify(x => this._lens.set(newValue, x));
    }
    _onSourceValue(x) {
        const prevValue = this.getValue();
        const next = this._lens.get(x);
        if (!this._eq(prevValue, next))
            this.next(next);
    }
    _subscription = null;
    _refCount = 0;
    subscribe(...args) {
        if (!this._subscription) {
            this._subscription = this._source.subscribe(x => this._onSourceValue(x));
        }
        this._refCount++;
        const sub = new Subscription(() => {
            if (--this._refCount <= 0 && this._subscription) {
                this._subscription.unsubscribe();
                this._subscription = null;
            }
        });
        sub.add(super.subscribe(...args));
        return sub;
    }
    unsubscribe() {
        if (this._subscription) {
            this._subscription.unsubscribe();
            this._subscription = null;
        }
        this._refCount = 0;
        super.unsubscribe();
    }
}
class AtomViewImpl extends AbstractReadOnlyAtom {
    _source;
    _getter;
    _eq;
    constructor(_source, _getter, _eq = structEq) {
        // @NOTE this is a major hack to optimize for not calling
        // _getter the extra time here. This makes the underlying
        // BehaviorSubject to have an `undefined` for it's current value.
        //
        // But it works because before somebody subscribes to this
        // atom, it will subscribe to the _source (which we expect to be a
        // descendant of BehaviorSubject as well), which will emit a
        // value right away, triggering our _onSourceValue.
        super(undefined);
        this._source = _source;
        this._getter = _getter;
        this._eq = _eq;
    }
    get() {
        // Optimization: in case we're already subscribed to the
        // source atom, the BehaviorSubject.getValue will return
        // an up-to-date computed lens value.
        //
        // This way we don't need to recalculate the view value
        // every time.
        return this._subscription
            ? this.getValue()
            : this._getter(this._source.get());
    }
    _onSourceValue(x) {
        const prevValue = this.getValue();
        const next = this._getter(x);
        if (!this._eq(prevValue, next))
            this.next(next);
    }
    _subscription = null;
    _refCount = 0;
    subscribe(...args) {
        if (!this._subscription) {
            this._subscription = this._source.subscribe(x => this._onSourceValue(x));
        }
        this._refCount++;
        const sub = new Subscription(() => {
            if (--this._refCount <= 0 && this._subscription) {
                this._subscription.unsubscribe();
                this._subscription = null;
            }
        });
        sub.add(super.subscribe(...args));
        return sub;
    }
    unsubscribe() {
        if (this._subscription) {
            this._subscription.unsubscribe();
            this._subscription = null;
        }
        this._refCount = 0;
        super.unsubscribe();
    }
}
export class CombinedAtomViewImpl extends AbstractReadOnlyAtom {
    _sources;
    _combineFn;
    _eq;
    constructor(_sources, _combineFn, _eq = structEq) {
        // @NOTE this is a major hack to optimize for not calling
        // _combineFn and .get for each source the extra time here.
        // This makes the underlying BehaviorSubject to have an
        // `undefined` for it's current value.
        //
        // But it works because before somebody subscribes to this
        // atom, it will subscribe to the _source (which we expect to be a
        // descendant of BehaviorSubject as well), which will emit a
        // value right away, triggering our _onSourceValue.
        super(undefined);
        this._sources = _sources;
        this._combineFn = _combineFn;
        this._eq = _eq;
    }
    get() {
        // Optimization: in case we're already subscribed to
        // source atoms, the BehaviorSubject.getValue will return
        // an up-to-date computed view value.
        //
        // This way we don't need to recalculate the view value
        // every time.
        return this._subscription
            ? this.getValue()
            : this._combineFn(this._sources.map(x => x.get()));
    }
    _onSourceValues(xs) {
        const prevValue = this.getValue();
        const next = this._combineFn(xs);
        if (!this._eq(prevValue, next))
            this.next(next);
    }
    _subscription = null;
    _refCount = 0;
    subscribe(...args) {
        if (!this._subscription) {
            this._subscription = combineLatest(this._sources)
                .subscribe(xs => this._onSourceValues(xs));
        }
        this._refCount++;
        const sub = new Subscription(() => {
            if (--this._refCount <= 0 && this._subscription) {
                this._subscription.unsubscribe();
                this._subscription = null;
            }
        });
        sub.add(super.subscribe(...args));
        return sub;
    }
    unsubscribe() {
        if (this._subscription) {
            this._subscription.unsubscribe();
            this._subscription = null;
        }
        this._refCount = 0;
        super.unsubscribe();
    }
}
//# sourceMappingURL=base.js.map